import React, { useEffect, useRef, useState } from "react";
import CustomerInfo from "./CustomerInfo";
import Stats from "./Stats";
import { Card } from "@arnsbogroup/react-voicearchive";
import Tabs from "components/TabsRoutes";
import ContactInfo from "./ContactInfo";
import DefaultValues from "./DefaultValues";
import OpenOrders from "./OpenOrders";
import ClosedOrders from "./ClosedOrders";
import Contacts from "./Contacts";
import Invoices from "./Invoices";
import FileSettings from "./FileSettings";
import CreateOrder from "./CreateOrder";
import { useRouteMatch } from 'react-router-dom'
import RequiredFields from './RequiredFields'
import messages from "./messages";
import { useIntl } from 'react-intl'
import useCurrentUser from 'hooks/useCurrentUser'
import Actions from './Actions'

const CustomerPage = ({ customerId }) => {
  const mounted = useRef(false);
  const [state, setState] = useState(null);
  const { path, url } = useRouteMatch()
  const [isCustomerDataComplete, setIsCustomerDataComplete] = useState(true);
  const { formatMessage } = useIntl()
  const { features = [] } = useCurrentUser()

  useEffect(() => {
    mounted.current = true;
    document.title = "Customers";

    if (mounted.current && parseInt(customerId)) {
      fetchCustomer();
    }
    
    return () => {
      mounted.current = false;
    };
  }, [customerId]);

  const fetchCustomer = () => {
    fetch(`/rest/v1/customer/${customerId}?includeRelations=1`)
    .then((response) => response.json())
    .then((data) => {
      setState(data);
    });
  }

  const onChange = async (newState, forceRefresh = false) => {
    let diff = Object.keys(newState).reduce((diff, key) => {
      if (state[key] === newState[key]) return diff;
      return { ...diff, [key]: newState[key] };
    }, {});

    if (!forceRefresh) {
      if (Object.keys(diff).length === 0 && diff.constructor === Object) {
        return;
      }
    }
    
    if (Object.keys(diff).includes('euvatid')) {

      const vatData = await validateEuVatId(newState.euvatid);
      if (!vatData) {
        toastr.error(formatMessage(messages.euVatIdNotValid));
      } else if (typeof vatData == "object") {
        const addressArr = vatData.address.split('\n')
        if (addressArr.length > 0) {
          if (!state.address || state.address == '') {
            diff = { ...diff, address: addressArr[0] }
          }

          const cityArr = addressArr[1] ? addressArr[1].split(' ') : []
          if (cityArr.length > 0) {
            if (!state.zip || state.zip == '') {
              diff = { ...diff, zip: cityArr[0] }
            }
            
            if (!state.city || state.city == '') {
              diff = { ...diff, city: cityArr[1] }
            }
          }
        }
        if (!state.name || state.name == '') {
          diff = { ...diff, name: vatData.name }
        }
      }
    }

    let prevState = state;
    setState(newState);

    fetch(`/rest/v1/customer/${customerId}`, {
      credentials: "same-origin",
      method: "PUT",
      body: JSON.stringify(diff),
    })
      .then(() => {
        toastr.success("Customer updated");
        fetchCustomer();
      })
      .catch((error) => {
        toastr.error(error);
        setState(prevState);
      });
  };

  const validateEuVatId = async (euVatId) => {
      if (+state.country.euMember) {
          if (euVatId == '') {
              return confirm(formatMessage(messages.confirmEmptyVatId));
          }
          toastr.warning(formatMessage(messages.validatingEuVatId))
          const cc = euVatId ? euVatId.substring(0,2) : ''
          const vatNum = euVatId ? euVatId.substring(2) : '';
          const params = new URLSearchParams(Object.entries({
              country: cc,
              vat_id: vatNum
          }))

          return await fetch(`/system/vies?${params}`, { credentials: 'same-origin' })
          .then((response) => response.json())
          .then((response) => {
              if (response.valid) {
                  return response;
              } else {
                  if (response.message) {
                      toastr.error(response.message)
                  }
                  return false;
              }
          }).catch((error) => {
              toastr.error(error)
              return false;
          })
      } else {
          return true;
      }
    }

  return (
    <div>
      {!!state &&
        <div className="bg-gray">
          
          <RequiredFields isCustomerDataComplete={setIsCustomerDataComplete} customer={state} />
          <div className="bg-gray-50 p-4">
            <Card className="bg-white">
              <CustomerInfo onChange={onChange} customer={state} />
              <div className="mb-4 py-4 md:py-0 md:px-4 md:order-none flex justify-center items-center md:w-1/6">
                <CreateOrder isCustomerDataComplete={isCustomerDataComplete} customer={state} />
                <Actions
                  customer={state}
                />
              </div>
            </Card>
          </div>
          <div>
          <Tabs
            className="bg-gray-50"
            childrenClassName="bg-white"
            tabsClassName="px-4"
            tabs={[
              {
                message: "Contact info",
                to: `${url}/contact-info`,
                route: {
                  path: `${path}/contact-info`,
                  render: (props) => (
                    <ContactInfo
                      onChange={onChange}
                      customer={state}
                    />
                  ),
                },
              },
              {
                message: "Default values",
                to: `${url}/default-values`,
                route: {
                  path: `${path}/default-values`,
                  render: (props) => (
                    <DefaultValues
                      onChange={onChange}
                      customer={state}
                    />
                  ),
                },
              },
              {
                message: "Open orders",
                to: `${url}/open-orders`,
                route: {
                  path: `${path}/open-orders`,
                  render: (props) => (
                    <OpenOrders
                      onChange={onChange}
                      customer={state}
                    />
                  ),
                },
              },
              {
                message: "Closed orders",
                to: `${url}/closed-orders`,
                route: {
                  path: `${path}/closed-orders`,
                  render: (props) => (
                    <ClosedOrders
                      onChange={onChange}
                      customer={state}
                    />
                  ),
                },
              },
              {
                message: "Contacts",
                to: `${url}/contacts`,
                route: {
                  path: `${path}/contacts`,
                  render: (props) => (
                    <Contacts
                      isCustomerDataComplete={isCustomerDataComplete}
                      onChange={onChange}
                      customer={state}
                    />
                  ),
                },
              },
              {
                message: "Invoices",
                to: `${url}/invoices`,
                route: {
                  path: `${path}/invoices`,
                  render: (props) => (
                    <Invoices
                      onChange={onChange}
                      customer={state}
                    />
                  ),
                },
              },
              {
                message: "File settings",
                to: `${url}/file-settings`,
                route: {
                  path: `${path}/file-settings`,
                  render: (props) => (
                    <FileSettings
                      onChange={onChange}
                      customer={state}
                    />
                  ),
                },
              },
              {
                message: "Stats",
                to: `${url}/stats`,
                route: {
                  path: `${path}/stats`,
                  render: (props) => (
                    <Stats
                      customer={state} 
                    />
                  ),
                },
              },
            ]}
          />
        </div>
      </div>
      }
    </div>
  );
};

export default CustomerPage;
